/** biome-ignore-all lint/complexity/noBannedTypes: used intentionally to allow any non-null object */

import isObject from 'lodash/isObject';
import type { ReactNode } from 'react';

import type { Resource, ResourceOperation } from './resources';

export interface ValueChange<T = string> {
  old_value?: T;
  new_value: T;
}

export interface IncrementalChange<T = string> {
  added: T[];
  removed?: T[];
}

export type Change<T = string> = ValueChange<T> | IncrementalChange<T>;

export type HistoryEntryElementMap = Record<string, (props: Change & { label: string }) => ReactNode>;

function isChange(input: unknown, expectedKeys: string[]): boolean {
  return isObject(input) && expectedKeys.some(expectedKey => !!(input as Record<string, undefined>)[expectedKey]);
}

export function isValueChange<T>(change: unknown): change is ValueChange<T> {
  return isChange(change, ['old_value', 'new_value']);
}

export function isIncrementalChange<T>(change: unknown): change is IncrementalChange<T> {
  return isChange(change, ['added', 'removed']);
}

export interface SetCreateChange {
  [setItem: string]: true;
}

export function isSetCreateChange(input: unknown): input is SetCreateChange {
  return (
    typeof input === 'object' &&
    input !== null &&
    !Array.isArray(input) &&
    Object.values(input).every(value => value === true)
  );
}

export type SetItemUpdateChange = ValueChange<true>;

export function isSetItemUpdateChange(input: unknown): input is SetItemUpdateChange {
  return isValueChange(input) && (input.new_value === true || input.old_value === true);
}

export type CreateAuditTrailWithAcl = CreateAuditTrail & {
  changes: { new_value: { acl?: { [key: string]: string } } };
};

export type CloneAuditTrailWithAcl = CloneAuditTrail & {
  changes: { new_value: { acl?: { [key: string]: string } } };
};

export interface CreateChanges<R extends {} = {}> {
  new_value: R | SetCreateChange;
}

export type UpdateChanges<R extends {} = {}> =
  | {
      [key in keyof R]?: Change;
    }
  | {
      [propertyNameWithSetItemName: string]: SetItemUpdateChange;
    };

interface BaseAuditTrail {
  obj_id: number | string;
  obj_type: Resource;
  user: {
    first_name?: string;
    last_name?: string;
    name?: string;
    email: string;
  };
  real_name?: string;
  real_user?: string;
  reason?: string;
  timestamp: string;
  trace_id?: string;
}

export interface CreateAuditTrail extends BaseAuditTrail {
  action: typeof ResourceOperation.CREATE;
  changes: CreateChanges;
}

export interface CloneAuditTrail extends BaseAuditTrail {
  action: typeof ResourceOperation.CLONE;
  changes: CreateChanges;
}

export interface UpdateAuditTrail extends BaseAuditTrail {
  action: typeof ResourceOperation.UPDATE;
  changes: UpdateChanges;
}

export interface ExpireAuditTrail extends BaseAuditTrail {
  action: typeof ResourceOperation.EXPIRE;
  changes: UpdateChanges;
}

export type AuditTrail = CreateAuditTrail | UpdateAuditTrail | CloneAuditTrail | ExpireAuditTrail;
